import { useEffect, type ComponentType } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useBrand } from '@/shared/brand/useBrand';
import { PageHeader } from '@/shared/components/PageHeader';
import { Icon, type IconName } from '@/shared/components/ui/Icon';
import type { MessageKey } from '@/shared/i18n/messages';
import { useI18n } from '@/shared/i18n/useT';
import { FormulasSection } from './help/FormulasSection';
import { ShortcutsSection } from './help/ShortcutsSection';

/**
 * In-app help for every signed-in member. It deliberately mirrors Settings:
 * sections are the single source of truth, the active one travels in `?h=`, and
 * on a phone the section list IS the screen until a section is picked.
 */
interface Section {
  readonly id: string;
  readonly titleKey: MessageKey;
  readonly descriptionKey: MessageKey;
  readonly icon: IconName;
  readonly Component: ComponentType;
}

const SECTIONS: readonly Section[] = [
  {
    id: 'formulas',
    titleKey: 'help.section.formulas.title',
    descriptionKey: 'help.section.formulas.description',
    icon: 'calculators',
    Component: FormulasSection,
  },
  {
    id: 'shortcuts',
    titleKey: 'help.section.shortcuts.title',
    descriptionKey: 'help.section.shortcuts.description',
    icon: 'grid',
    Component: ShortcutsSection,
  },
];

export function HelpPage() {
  const { t } = useI18n();
  const brand = useBrand();
  const [params, setParams] = useSearchParams();

  const requested = params.get('h');
  const selected = SECTIONS.find((section) => section.id === requested) ?? null;
  const active = selected ?? SECTIONS[0]!;
  const { Component } = active;

  useEffect(() => {
    document.title = `${t('help.title')} · ${brand.name}`;
  }, [t, brand.name]);

  return (
    <div className="pb-6 sm:pb-8">
      <div className={selected ? 'hidden md:block' : 'block'}>
        <PageHeader title={t('help.title')} description={t('help.subtitle')} />
      </div>

      <div className="flex flex-col gap-6 md:flex-row">
        <nav
          aria-label={t('help.title')}
          className={`w-full shrink-0 md:block md:w-64 ${selected ? 'hidden' : 'block'}`}
        >
          <ul className="flex flex-col gap-1">
            {SECTIONS.map((section) => {
              const isActive = section.id === active.id;
              return (
                <li key={section.id}>
                  <button
                    type="button"
                    onClick={() => setParams({ h: section.id })}
                    aria-current={selected?.id === section.id ? 'page' : undefined}
                    className={`tap-target flex w-full items-center gap-3 rounded-full border border-transparent px-3 py-2 text-left transition-colors ${
                      isActive
                        ? 'md:border-border md:bg-accent-soft md:font-medium md:text-accent'
                        : 'hover:bg-surface-3'
                    }`}
                  >
                    <Icon name={section.icon} />
                    <span className="min-w-0">
                      <span className="block truncate text-sm text-fg">
                        {t(section.titleKey)}
                      </span>
                      <span className="block truncate text-xs text-muted">
                        {t(section.descriptionKey)}
                      </span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>

        <section className={`min-w-0 flex-1 ${selected ? 'block' : 'hidden md:block'}`}>
          {/* Back to the menu — mobile only, where the menu was replaced. */}
          <button
            type="button"
            onClick={() => setParams({})}
            className="tap-target mb-3 inline-flex items-center gap-2 text-sm text-muted hover:text-fg md:hidden"
          >
            <Icon name="menu" />
            {t('common.back')}
          </button>

          <h2 className="mb-3 text-lg font-semibold text-fg md:hidden">
            {t(active.titleKey)}
          </h2>

          <Component />
        </section>
      </div>
    </div>
  );
}
